import React, { useState, useEffect } from 'react';
import { Col, Statistic, Empty, Tag, DatePicker, Select, Button, message, Drawer, Input, Switch } from 'antd';
import { SearchOutlined, RightOutlined, DownloadOutlined } from '@ant-design/icons';
import BookingService from '../../../services/BookingService';
import HeartLoader from '../../../components/shared/HeartLoader';
import {
  CalendarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { usePageHeader } from '../../../layouts/AdminLayout';
import {
  StyledAnalyticsTabs,
  TabsAndFiltersRow,
  FiltersContainer,
  ChartCard,
  StatCard,
  StatsRow,
  ChartsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  iconColors,
  tableCellStyles,
  DrawerTable
} from '../styled';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Events = ({ dateRange: propDateRange, timeRange: propTimeRange }) => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('comparison');
  const [dateRange, setDateRange] = useState(propDateRange || [dayjs().subtract(90, 'days'), dayjs()]);
  const [quickRange, setQuickRange] = useState(propTimeRange || '3months');
  
  const [comparisonData, setComparisonData] = useState(null);
  const [capacityData, setCapacityData] = useState(null);
  const [coverageData, setCoverageData] = useState(null);
  const [effectivenessData, setEffectivenessData] = useState(null);
  const [excludeArchived, setExcludeArchived] = useState(true);

  // Event bookings drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bookingsData, setBookingsData] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsPagination, setBookingsPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [bookingsSearch, setBookingsSearch] = useState('');
  const [exportingBookings, setExportingBookings] = useState(false);
  const [bookingsSortField, setBookingsSortField] = useState('bookingDate');
  const [bookingsSortOrder, setBookingsSortOrder] = useState('desc');
  const [drawerDateRange, setDrawerDateRange] = useState(dateRange);
  const [drawerQuickRange, setDrawerQuickRange] = useState(quickRange);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      };
      const response = await api.getBlob('/screening/exports/event-summary', params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `event-summary-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Event summary exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export event summary');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPageHeader({ title: 'Events Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  useEffect(() => {
    loadData();
  }, [dateRange, excludeArchived]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        ...(excludeArchived && { excludeArchived: 'true' })
      };

      const [compRes, capRes, covRes, effRes] = await Promise.all([
        api.get('/screening/analytics/events/comparison', params),
        api.get('/screening/analytics/events/capacity', params),
        api.get('/screening/analytics/events/screening-coverage', params),
        api.get('/screening/analytics/events/type-effectiveness', params)
      ]);

      const extractData = (res) => res.data?.success !== undefined 
        ? (res.data.success ? res.data.data : null) 
        : res.data;

      if (extractData(compRes)) setComparisonData(extractData(compRes));
      if (extractData(capRes)) setCapacityData(extractData(capRes));
      if (extractData(covRes)) setCoverageData(extractData(covRes));
      if (extractData(effRes)) setEffectivenessData(extractData(effRes));
    } catch (error) {
      console.error('Error loading events data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = async (record) => {
    if (record.eventId === '000000000000000000000000') return; // Skip walk-ins
    setSelectedEvent(record);
    setDrawerVisible(true);
    setBookingsSearch('');
    setDrawerDateRange(dateRange);
    setDrawerQuickRange(quickRange);
    await loadBookings(record.eventId, 1, '', bookingsSortField, bookingsSortOrder, dateRange);
  };

  const loadBookings = async (eventTypeId, page = 1, search = '', sortField = bookingsSortField, sortOrder = bookingsSortOrder, range = drawerDateRange) => {
    setBookingsLoading(true);
    try {
      const result = await BookingService.getAllBookings({
        eventTypeId,
        page,
        limit: 20,
        search: search || undefined,
        sortField,
        sortOrder,
        includeAllStatuses: true,
        startDate: range[0]?.format('YYYY-MM-DD'),
        endDate: range[1]?.format('YYYY-MM-DD')
      });
      if (result.success) {
        setBookingsData(result.data.bookings || []);
        setBookingsPagination(result.data.pagination || { page, limit: 20, total: 0 });
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleBookingsTableChange = (pagination, filters, sorter) => {
    if (sorter?.field || sorter?.columnKey) {
      const fieldMap = { client: 'userInfo.firstName', bookingDate: 'bookingDate', status: 'status' };
      const newSortField = fieldMap[sorter.columnKey] || 'bookingDate';
      const newSortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
      setBookingsSortField(newSortField);
      setBookingsSortOrder(newSortOrder);
      loadBookings(selectedEvent?.eventId, pagination.current, bookingsSearch, newSortField, newSortOrder);
    } else {
      loadBookings(selectedEvent?.eventId, pagination.current, bookingsSearch);
    }
  };

  const handleBookingsSearch = (value) => {
    setBookingsSearch(value);
    loadBookings(selectedEvent?.eventId, 1, value);
  };

  const handleDrawerQuickRange = (value) => {
    setDrawerQuickRange(value);
    let newRange;
    const now = dayjs();
    switch (value) {
      case 'week': newRange = [now.subtract(7, 'days'), now]; break;
      case 'month': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(90, 'days'), now]; break;
      case '6months': newRange = [now.subtract(6, 'months'), now]; break;
      case 'year': newRange = [now.subtract(1, 'year'), now]; break;
      case 'all': newRange = [dayjs('2020-01-01'), now]; break;
      case 'Q1': newRange = [dayjs(`${now.year()}-01-01`), dayjs(`${now.year()}-03-31`)]; break;
      case 'Q2': newRange = [dayjs(`${now.year()}-04-01`), dayjs(`${now.year()}-06-30`)]; break;
      case 'Q3': newRange = [dayjs(`${now.year()}-07-01`), dayjs(`${now.year()}-09-30`)]; break;
      case 'Q4': newRange = [dayjs(`${now.year()}-10-01`), dayjs(`${now.year()}-12-31`)]; break;
      default: newRange = [now.subtract(90, 'days'), now];
    }
    setDrawerDateRange(newRange);
    loadBookings(selectedEvent?.eventId, 1, bookingsSearch, bookingsSortField, bookingsSortOrder, newRange);
  };

  const handleExportBookings = async () => {
    if (!selectedEvent) return;
    setExportingBookings(true);
    try {
      const result = await BookingService.getAllBookings({
        eventTypeId: selectedEvent.eventId,
        page: 1,
        limit: 10000,
        sortField: 'bookingDate',
        sortOrder: 'desc',
        includeAllStatuses: true,
        startDate: drawerDateRange[0]?.format('YYYY-MM-DD'),
        endDate: drawerDateRange[1]?.format('YYYY-MM-DD')
      });
      if (result.success && result.data.bookings?.length) {
        const allBookings = result.data.bookings;
        const csvHeaders = ['Name', 'Email', 'Phone', 'Booking Date', 'Time Slot', 'Status', 'Confirmation Code'];
        const csvRows = allBookings.map(b => [
          `"${(b.userInfo?.firstName || '')} ${(b.userInfo?.lastName || '')}"`,
          `"${b.userInfo?.email || ''}"`,
          `"${b.userInfo?.phone || ''}"`,
          `"${dayjs(b.bookingDate).format('MMM D, YYYY')}"`,
          `"${b.timeSlot?.startTime ? dayjs(b.timeSlot.startTime).format('h:mm A') + ' - ' + dayjs(b.timeSlot.endTime).format('h:mm A') : ''}"`,
          `"${b.status || ''}"`,
          `"${b.confirmationCode || ''}"`
        ]);
        const csv = [csvHeaders.join(','), ...csvRows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedEvent.title}-bookings-${dayjs().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success(`Exported ${allBookings.length} bookings`);
      } else {
        message.info('No bookings to export');
      }
    } catch (error) {
      console.error('Error exporting bookings:', error);
      message.error('Failed to export bookings');
    } finally {
      setExportingBookings(false);
    }
  };

  const bookingColumns = [
    {
      title: 'Event Name',
      key: 'eventName',
      width: 150,
      render: (_, record) => (
        <div style={{ fontWeight: 'bold' }}>
          {record.eventLocation?.eventName || record.userInfo?.customData?.businessEventName || '-'}
        </div>
      )
    },
    {
      title: 'Client',
      key: 'client',
      width: 200,
      sorter: true,
      render: (_, record) => (
        <div>
          <div>
            {record.userInfo?.firstName} {record.userInfo?.lastName}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.userInfo?.email}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.userInfo?.phone}
          </div>
        </div>
      )
    },
    {
      title: 'Event Time',
      key: 'bookingDate',
      width: 200,
      sorter: true,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {dayjs(record.bookingDate).format('MMM D, YYYY')}
            {record.endDate && record.isMultiDay && (
              <span> – {dayjs(record.endDate).format('MMM D, YYYY')}</span>
            )}
          </div>
          <div style={{ color: '#666' }}>
            {record.timeSlot ?
              `${dayjs(record.timeSlot.startTime).format('h:mm A')} - ${dayjs(record.timeSlot.endTime).format('h:mm A')}` :
              dayjs(record.bookingDate).format('h:mm A')
            }
          </div>
        </div>
      )
    },
    {
      title: 'Members',
      key: 'membersScreened',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <div style={{ textAlign: 'center', fontWeight: 600 }}>
          {record.membersScreened || 0}
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      sorter: true,
      render: (_, record) => {
        const colorMap = {
          confirmed: 'blue',
          pending: 'gold',
          completed: 'green',
          cancelled: 'red',
          'no-show': 'orange'
        };
        return <Tag color={colorMap[record.status] || 'default'}>{record.status}</Tag>;
      }
    }
  ];

  const renderComparison = () => {
    if (!comparisonData) return <Empty description="No data available" />;

    const columns = [
      { 
        title: 'Event', 
        dataIndex: 'title', 
        key: 'title',
        render: (text, record) => (
          <div>
            <div style={tableCellStyles.title}>{text}</div>
            <div style={tableCellStyles.subtitle}>
              {dayjs(record.date).format('MMM D, YYYY')}
            </div>
          </div>
        )
      },
      { title: 'Bookings', dataIndex: 'registered', key: 'registered', sorter: (a, b) => a.registered - b.registered },
      { title: 'Members', dataIndex: 'attended', key: 'attended', sorter: (a, b) => a.attended - b.attended },
      { title: 'Completed', dataIndex: 'completed', key: 'completed' },
      { title: 'High Risk', dataIndex: 'highRiskDetected', key: 'highRiskDetected', render: (val) => <Tag color={val > 0 ? 'red' : 'default'}>{val}</Tag> },
      { title: 'Attendance Rate', dataIndex: 'attendanceRate', key: 'attendanceRate', render: (val) => `${val}%`, sorter: (a, b) => parseFloat(a.attendanceRate) - parseFloat(b.attendanceRate) },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, record) => record.eventId !== '000000000000000000000000' ? (
          <span
            style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            onClick={(e) => { e.stopPropagation(); handleEventClick(record); }}
          >
            View Bookings <RightOutlined style={{ fontSize: 11, marginLeft: 4 }} />
          </span>
        ) : null
      }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Events" value={comparisonData.summary.totalEvents} prefix={<CalendarOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Booked" value={comparisonData.summary.totalRegistered} prefix={<TeamOutlined style={iconColors.purple} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Members" value={comparisonData.summary.totalAttended} prefix={<CheckCircleOutlined style={iconColors.success} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Overall Attendance" value={comparisonData.summary.overallAttendanceRate} suffix="%" valueStyle={{ color: parseFloat(comparisonData.summary.overallAttendanceRate) > 70 ? '#108043' : '#faad14' }} />
            </StatCard>
          </Col>
        </StatsRow>

        <DrawerTable
          dataSource={comparisonData.events}
          columns={columns}
          rowKey="eventId"
          pagination={{ pageSize: 10, position: ['bottomRight'] }}
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: () => handleEventClick(record),
            style: { cursor: record.eventId !== '000000000000000000000000' ? 'pointer' : 'default' }
          })}
        />
      </>
    );
  };

  const renderCapacity = () => {
    if (!capacityData) return <Empty description="No data available" />;

    const capOptions = {
      chart: { type: 'bar', toolbar: { show: false }, stacked: false },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '70%' } },
      colors: ['#8c8c8c', '#1890ff', '#108043'],
      xaxis: { 
        categories: capacityData.events?.slice(0, 10).map(e => e.title?.substring(0, 15) + '...') || [],
        labels: { rotate: -45 }
      },
      dataLabels: { enabled: false },
      legend: { position: 'top' },
      tooltip: { shared: true, intersect: false }
    };
    const capSeries = [
      { name: 'Capacity', data: capacityData.events?.slice(0, 10).map(e => e.capacity) || [] },
      { name: 'Bookings', data: capacityData.events?.slice(0, 10).map(e => e.registered) || [] },
      { name: 'Walk-ins', data: capacityData.events?.slice(0, 10).map(e => e.walkIns) || [] }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Capacity" value={capacityData.summary.totalCapacity} prefix={<TeamOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Booked" value={capacityData.summary.totalRegistered} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Avg Utilization" value={capacityData.summary.avgUtilization} suffix="%" valueStyle={{ color: parseFloat(capacityData.summary.avgUtilization) > 70 ? '#108043' : '#faad14' }} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Walk-In Rate" value={capacityData.summary.avgWalkInRate} suffix="%" />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartCard title="Capacity Utilization by Event">
          <Chart options={capOptions} series={capSeries} type="bar" height={400} />
        </ChartCard>
      </>
    );
  };

  const renderCoverage = () => {
    if (!coverageData) return <Empty description="No data available" />;

    const covOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: coverageData.coverage?.map(c => c.color) || ['#1890ff'],
      xaxis: { categories: coverageData.coverage?.map(c => c.label) || [] },
      dataLabels: { enabled: true },
      legend: { show: false }
    };
    const covSeries = [
      { name: 'Tests Completed', data: coverageData.coverage?.map(c => c.testsCompleted) || [] }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={12}>
            <StatCard>
              <Statistic title="Completed Screenings" value={coverageData.summary?.totalCompletedVisits || 0} prefix={<ExperimentOutlined style={iconColors.success} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <StatCard>
              <Statistic title="Most Popular Screening" value={coverageData.summary?.mostPopularScreening?.replace(/([A-Z])/g, ' $1').trim() || 'N/A'} />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartCard title="Screening Types Performed">
          <Chart options={covOptions} series={covSeries} type="bar" height={350} />
        </ChartCard>
      </>
    );
  };

  const renderEffectiveness = () => {
    if (!effectivenessData) return <Empty description="No data available" />;

    // Pie chart for event types
    const pieOptions = {
      chart: { type: 'donut' },
      labels: effectivenessData.effectiveness?.map(e => e.label) || [],
      colors: ['#1890ff', '#108043', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` }
    };
    const pieSeries = effectivenessData.effectiveness?.map(e => e.count) || [];

    // Bar chart for completion/cancellation rates
    const barOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#52c41a', '#ff4d4f'],
      xaxis: { 
        categories: effectivenessData.effectiveness?.map(e => e.label) || [],
        labels: { rotate: -45 }
      },
      yaxis: { 
        labels: { formatter: (val) => `${val}%` },
        max: 100 
      },
      dataLabels: { enabled: false },
      legend: { position: 'top' }
    };
    const barSeries = [
      { name: 'Completion Rate', data: effectivenessData.effectiveness?.map(e => parseFloat(e.completionRate) || 0) || [] },
      { name: 'Cancellation Rate', data: effectivenessData.effectiveness?.map(e => parseFloat(e.cancellationRate) || 0) || [] }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic title="Total Bookings" value={effectivenessData.summary?.totalBookings || 0} prefix={<CalendarOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic title="Most Common Type" value={effectivenessData.summary?.mostCommonType || 'N/A'} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic title="Best Completion Rate" value={effectivenessData.summary?.bestCompletionRate || 'N/A'} prefix={<RiseOutlined style={iconColors.success} />} />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Bookings by Event Type">
              <Chart options={pieOptions} series={pieSeries} type="donut" height={350} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Completion & Cancellation Rates">
              <Chart options={barOptions} series={barSeries} type="bar" height={350} />
            </ChartCard>
          </Col>
        </ChartsRow>

        <DrawerTable
          dataSource={effectivenessData.effectiveness || []}
          columns={[
            {
              title: 'Event Type',
              dataIndex: 'label',
              key: 'label',
              render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>
            },
            {
              title: 'Bookings',
              dataIndex: 'totalBooked',
              key: 'totalBooked',
              width: 110,
              sorter: (a, b) => a.totalBooked - b.totalBooked
            },
            {
              title: 'Members',
              dataIndex: 'totalAttended',
              key: 'totalAttended',
              width: 110,
              sorter: (a, b) => a.totalAttended - b.totalAttended
            },
            {
              title: 'Completed',
              dataIndex: 'totalCompleted',
              key: 'totalCompleted',
              width: 110,
              sorter: (a, b) => a.totalCompleted - b.totalCompleted,
              render: (val) => <Tag color={val > 0 ? 'green' : 'default'}>{val}</Tag>
            },
            {
              title: 'Cancelled',
              dataIndex: 'totalCancelled',
              key: 'totalCancelled',
              width: 110,
              sorter: (a, b) => a.totalCancelled - b.totalCancelled,
              render: (val) => <Tag color={val > 0 ? 'red' : 'default'}>{val}</Tag>
            },
            {
              title: 'Completion Rate',
              dataIndex: 'completionRate',
              key: 'completionRate',
              width: 140,
              sorter: (a, b) => parseFloat(a.completionRate) - parseFloat(b.completionRate),
              render: (val) => <span style={{ color: parseFloat(val) >= 70 ? '#52c41a' : '#faad14', fontWeight: 600 }}>{val}%</span>
            },
            {
              title: 'Cancellation Rate',
              dataIndex: 'cancellationRate',
              key: 'cancellationRate',
              width: 150,
              sorter: (a, b) => parseFloat(a.cancellationRate) - parseFloat(b.cancellationRate),
              render: (val) => <span style={{ color: parseFloat(val) > 20 ? '#ff4d4f' : '#666', fontWeight: 500 }}>{val}%</span>
            }
          ]}
          rowKey="type"
          size="small"
          pagination={false}
          style={{ marginTop: 24 }}
        />
      </>
    );
  };

  const subTabItems = [
    { key: 'comparison', label: 'Event Comparison' },
    { key: 'capacity', label: 'Capacity Utilization' },
    { key: 'coverage', label: 'Screening Coverage' },
    { key: 'effectiveness', label: 'Event Types' }
  ];

  const renderContent = () => {
    switch (activeSubTab) {
      case 'capacity': return renderCapacity();
      case 'coverage': return renderCoverage();
      case 'effectiveness': return renderEffectiveness();
      default: return renderComparison();
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '7days': newRange = [now.subtract(7, 'days'), now]; break;
      case '14days': newRange = [now.subtract(14, 'days'), now]; break;
      case '30days': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(3, 'months'), now]; break;
      case 'Q1': newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  if (loading && !comparisonData) {
    return (
      <PageContainer>
        <HeartLoader size="48px" fullHeight />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TabsAndFiltersRow className="filters-animate-in">
        <StyledAnalyticsTabs activeKey={activeSubTab} onChange={setActiveSubTab} items={subTabItems} />
        <FiltersContainer>
          <Select value={quickRange} onChange={handleQuickRangeChange} style={selectStyles} size="large">
            <Option value="7days">Last 7 days</Option>
            <Option value="14days">Last 14 days</Option>
            <Option value="30days">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="custom">Custom</Option>
          </Select>
          <RangePicker value={dateRange} onChange={handleDateRangeChange} format="MM/DD/YY" style={rangePickerStyles} size="large" allowClear={false} />
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting} size="large">Export</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={excludeArchived} onChange={setExcludeArchived} />
            <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Exclude Archived</span>
          </div>
        </FiltersContainer>
      </TabsAndFiltersRow>

      {loading ? <HeartLoader size="32px" padding="40px" /> : <div className="content-animate-in">{renderContent()}</div>}

      <Drawer
        title={
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{selectedEvent?.title}</div>
            <div style={{ fontSize: 13, color: '#666', fontWeight: 400 }}>
              Bookings · {bookingsPagination.total || 0} total · {drawerDateRange[0]?.format('MMM D, YYYY')} – {drawerDateRange[1]?.format('MMM D, YYYY')}
            </div>
          </div>
        }
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={800}
        destroyOnClose
        styles={{ body: { padding: '16px 24px 24px' } }}
      >
        {selectedEvent && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1, background: '#f0f5ff', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>{selectedEvent.registered ?? '-'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Bookings</div>
            </div>
            <div style={{ flex: 1, background: '#f6ffed', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a' }}>{selectedEvent.attended ?? '-'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Members</div>
            </div>
            <div style={{ flex: 1, background: '#e6f7ff', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#13c2c2' }}>{selectedEvent.completed ?? '-'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Completed</div>
            </div>
          </div>
        )}
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            value={drawerQuickRange}
            onChange={handleDrawerQuickRange}
            style={{ width: 140, height: 40 }}
          >
            <Option value="week">Last 7 Days</Option>
            <Option value="month">Last 30 Days</Option>
            <Option value="3months">Last 90 Days</Option>
            <Option value="6months">Last 6 Months</Option>
            <Option value="year">Last Year</Option>
            <Option value="all">All Time</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
          </Select>
          <Input
            placeholder="Search by name, email, phone..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={bookingsSearch}
            onChange={(e) => {
              setBookingsSearch(e.target.value);
              if (!e.target.value) handleBookingsSearch('');
            }}
            onPressEnter={(e) => handleBookingsSearch(e.target.value)}
            style={{ flex: 1, height: 40 }}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportBookings}
            loading={exportingBookings}
          >
            Export
          </Button>
        </div>
        <DrawerTable
          dataSource={bookingsData}
          columns={bookingColumns}
          rowKey={(r) => r._id || r.confirmationCode}
          loading={bookingsLoading}
          className="event-management-pagination"
          scroll={{ x: 800 }}
          pagination={{
            current: bookingsPagination.page,
            pageSize: bookingsPagination.limit,
            total: bookingsPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bookings`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleBookingsTableChange}
        />
      </Drawer>
    </PageContainer>
  );
};

export default Events;
